/* eslint-disable max-len */
import {
    Download,
    FilterList,
    Folder,
    MoreVert,
    Search,
    Send,
    Visibility
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Menu,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { getPacotesFormularios, PacoteFilters, PacoteAPI, PacotesApiResponse } from 'api/forms/pacote_formularios.api';
import { IUser } from 'modules/auth/interfaces/storage.interface';
import useMunicipalManagersStore from 'stores/forms/municipalManagers.store';
import useMunicipiosStore from 'stores/municipios.store';
import { formatDateDisplayCuiaba } from 'utils/timezone.utils';
import { ROUTES } from 'routes/constants';
import { useNavigate } from 'react-router-dom';

interface PacoteStateManagerProps {
    user: IUser;
}



interface Pacote {
    uuid: string;
    numero: string;
    ano: string;
    gerente: string;
    municipio: string;
    dataEnvio: string;
    status: string;
}

interface FilterState {
    numeroPacote: string;
    gerente: string;
    municipio: string;
    data: string;
}

export default function PacoteStateManager({ user }: PacoteStateManagerProps): JSX.Element {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(1); // 0 = Listar, 1 = Filtrar
    const [pacotes, setPacotes] = useState<Pacote[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedPacote, setSelectedPacote] = useState<string | null>(null);

    const [filters, setFilters] = useState<FilterState>({
        numeroPacote: '',
        gerente: '',
        municipio: '',
        data: ''
    });

    // Store de municípios
    const { municipios, getMunicipios } = useMunicipiosStore();

    // Store de gerentes municipais
    const { municipalManagers, getMunicipalManagers } = useMunicipalManagersStore();

    const itemsPerPage = 10;

    const getPacotes = async (filterParams?: FilterState, page = 1) => {
        setLoading(true);
        try {
            const apiFilters: PacoteFilters = {
                ...filterParams,
                page,
                limit: itemsPerPage
            };

            const response = await getPacotesFormularios(apiFilters);
            console.log('response', response);

            // Nova estrutura da API tipada
            const apiData: PacotesApiResponse = response.dados || response;

            // Transformar dados da API em formato esperado
            const pacotesFormatados: Pacote[] = apiData.data?.map((item: PacoteAPI) => {
                // Usar dados reais da API com tipagem correta
                const dataEnvio = formatDateDisplayCuiaba(item.data_envio);

                return {
                    uuid: item.uuid,
                    numero: item.pacote_nome,
                    ano: new Date(item.data_envio).getFullYear().toString(),
                    gerente: item.responsavel.name,
                    municipio: item.institution.name,
                    dataEnvio,
                    status: 'Cadastrado' // Status padrão, ajustar se vier da API
                };
            }) || [];

            setPacotes(pacotesFormatados);

            // Usar informações de paginação da API
            setTotalItems(apiData.total);
            setTotalPages(apiData.totalPages);
        } catch (error) {
            console.error('Erro ao buscar pacotes:', error);
            setPacotes([]);
            setTotalItems(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleFilterChange = (field: keyof FilterState, value: any) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const applyFilters = async () => {
        // Verificar se há filtros aplicados
        const hasFilters = filters.numeroPacote || filters.gerente || filters.municipio || filters.data;

        if (hasFilters) {
            await getPacotes(filters, 1);
        } else {
            await getPacotes(undefined, 1);
        }

        setCurrentPage(1);
        setActiveTab(0); // Voltar para aba Listar após filtrar
    };

    const clearFilters = async () => {
        const clearedFilters = {
            numeroPacote: '',
            gerente: '',
            municipio: '',
            data: ''
        };

        setFilters(clearedFilters);
        await getPacotes(undefined, 1);
        setCurrentPage(1);
    };

    const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, pacoteUuid: string) => {
        setActionMenuAnchor(event.currentTarget);
        setSelectedPacote(pacoteUuid);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchor(null);
        setSelectedPacote(null);
    };

    const handleAction = (action: string) => {
        console.log(`Ação ${action} executada para pacote ${selectedPacote}`);

        switch (action) {
            case 'visualizar':
                navigate(`${ROUTES.PACOTE_FORMULARIOS_ID.replace(':uuid', selectedPacote!)}`);
                break;

            default:
                break;
        }
        handleActionMenuClose();
        // Implementar lógica das ações aqui
    };

    const handlePageChange = async (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);

        // Verificar se há filtros aplicados
        const hasFilters = filters.numeroPacote || filters.gerente || filters.municipio || filters.data;

        if (hasFilters) {
            await getPacotes(filters, page);
        } else {
            await getPacotes(undefined, page);
        }
    };

    useEffect(() => {
        getPacotes(undefined, 1);
        getMunicipios();
        getMunicipalManagers();
    }, []);

    return (
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 2 }}>
            {/* Cabeçalho */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <Folder sx={{ fontSize: 48, color: '#8B4513', mr: 1 }} />
                    <Box sx={{ width: 20, height: 30, bgcolor: '#DAA520', borderRadius: 1 }} />
                </Box>

                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Consultar Pacotes das Prefeituras
                </Typography>

                <Typography variant="body1" color="text.secondary">
                    Gerencie todos os Pacotes enviados pela Prefeitura para o sistema
                </Typography>
            </Box>

            {/* Abas */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab
                        icon={<Search />}
                        label="Listar"
                        iconPosition="start"
                    />
                    <Tab
                        icon={<FilterList />}
                        label="Filtrar"
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            {/* Conteúdo das Abas */}
            {activeTab === 1 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Você pode buscar através dos critérios abaixo.
                        </Typography>

                        <Stack spacing={3}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField
                                    fullWidth
                                    label="Número do Pacote"
                                    placeholder="Número do Pacote"
                                    value={filters.numeroPacote}
                                    onChange={(e) => handleFilterChange('numeroPacote', e.target.value)}
                                />

                                <FormControl fullWidth>
                                    <InputLabel>Gerente Municipal</InputLabel>
                                    <Select
                                        value={filters.gerente}
                                        onChange={(e) => handleFilterChange('gerente', e.target.value)}
                                        label="Gerente Municipal"
                                    >
                                        {municipalManagers.map((gerente: any, index: number) => (
                                            <MenuItem key={index} value={gerente}>
                                                {gerente}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Município</InputLabel>
                                    <Select
                                        value={filters.municipio}
                                        onChange={(e) => handleFilterChange('municipio', e.target.value)}
                                        label="Município"
                                    >
                                        {municipios.map((municipio: any) => (
                                            <MenuItem key={municipio.uuid} value={municipio.name}>
                                                {municipio.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label="Data"
                                    type="date"
                                    value={filters.data}
                                    onChange={(e) => handleFilterChange('data', e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Stack>

                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button variant="outlined" onClick={clearFilters}>
                                    Limpar Filtros
                                </Button>
                                <Button variant="contained" onClick={applyFilters}>
                                    Aplicar Filtros
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Lista de Pacotes */}
            {activeTab === 0 && (
                <Box>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {pacotes.length > 0 ? (
                                <Stack spacing={2}>
                                    {pacotes.map((pacote: Pacote) => (
                                        <Card key={pacote.uuid} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                                            <CardContent>
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <Folder sx={{ fontSize: 40, color: '#8B4513' }} />

                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                            Pacotes: {pacote.numero}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Gerente: {pacote.gerente}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Município: {pacote.municipio}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            Data do Envio: {' '}
                                                            <Chip
                                                                label={pacote.dataEnvio}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: '#FF8C00',
                                                                    color: 'white',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            />
                                                        </Typography>
                                                    </Box>

                                                    <Button
                                                        variant="outlined"
                                                        endIcon={<MoreVert />}
                                                        onClick={(e) => handleActionMenuOpen(e, pacote.uuid)}
                                                        sx={{ minWidth: 100 }}
                                                    >
                                                        Ações
                                                    </Button>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}

                                    {/* Paginação */}
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                        <Pagination
                                            count={totalPages}
                                            page={currentPage}
                                            onChange={handlePageChange}
                                            color="primary"
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Box>
                                </Stack>
                            ) : (
                                <Box sx={{ textAlign: 'center', p: 4 }}>
                                    <Typography variant="h6" color="text.secondary">
                                        Nenhum pacote encontrado
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* Menu de Ações */}
            <Menu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={handleActionMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => handleAction('visualizar')}>
                    <Visibility sx={{ mr: 1 }} fontSize="small" />
                    Visualizar Detalhes
                </MenuItem>

            </Menu>
        </Box>
    );
}   
